/**
 * Timestamps that survive being placed in an Arabic sentence.
 *
 * ## Why not `toLocaleString`
 *
 * `new Date(x).toLocaleString("ar")` produces something like `18‏/8‏/2026, 1:39:00 م` — and those
 * are not ordinary slashes. ICU inserts U+200F RIGHT-TO-LEFT MARK between the date fields, so the
 * string carries its own embedded direction changes. Dropped into an RTL table cell it renders as
 * `1:39:00 2026/8/18 م`, with the time before the date. Wrapping it in `<bdi>` isolates it from the
 * text around it but does nothing about its insides. Adding `dir="ltr"` on top produces a third
 * arrangement — `18م 1:39:00 2026/8/` — because the explicit marks now fight the base direction.
 *
 * The other three screens in this repository avoided all of that by calling `toLocaleString()` with
 * no locale, which uses the *browser's* locale rather than the employee's chosen language. That is
 * not a fix, it is the bug not yet triggered: on an Arabic-locale workstation they render exactly
 * the same scramble.
 *
 * ## What this does instead
 *
 * Builds the string from numeric parts in a fixed `YYYY-MM-DD HH:mm` order, with Latin digits and no
 * locale prose, so there is nothing for the bidi algorithm to reorder. That is also the format an
 * administrator scanning a column of timestamps actually wants: unambiguous about which number is
 * the day, and visually sortable. Month names and AM/PM markers are what make a timestamp read as a
 * sentence, and a table cell is not a sentence.
 *
 * Render the result inside `dir="ltr"` so the surrounding RTL text cannot pull it apart.
 */

/** Fixed field order, so a column of these lines up and compares. */
const PARTS = ["year", "month", "day", "hour", "minute"] as const;

/**
 * `en-GB` for the calendar arithmetic only, never for the wording.
 *
 * Pinned rather than taken from the interface language because the *format* is deliberately not
 * localized — only the timezone conversion matters, and that comes from the runtime. Passing the
 * employee's language here would reintroduce the Arabic-Indic digits and direction marks this whole
 * module exists to avoid.
 */
const FORMAT_LOCALE = "en-GB";

const formatter = new Intl.DateTimeFormat(FORMAT_LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * `2026-08-18 13:39`, or the fallback for a value that is not a date.
 *
 * @param value ISO 8601 string from the API, or null.
 * @param fallback shown when there is no timestamp — localized by the caller, because "never" is
 *   prose and belongs in the locale files.
 */
export function formatTimestamp(value: string | null | undefined, fallback = "—"): string {
  if (!value) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  const found: Partial<Record<(typeof PARTS)[number], string>> = {};
  for (const part of formatter.formatToParts(date)) {
    if ((PARTS as readonly string[]).includes(part.type)) {
      found[part.type as (typeof PARTS)[number]] = part.value;
    }
  }

  const { year, month, day, hour, minute } = found;
  if (!year || !month || !day || !hour || !minute) return fallback;

  return `${year}-${month}-${day} ${hour}:${minute}`;
}

/** Bidi control characters. Present in the output means the reordering bug is back. */
export const BIDI_CONTROLS = /[‎‏‪-‮⁦-⁩]/;
